import { saveAs } from 'file-saver'
import { format } from 'date-fns'
import makeStyles from '@mui/styles/makeStyles'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import { convertArrayToCsvString } from '../../util'
import { formatNumber, sortBy } from '../../util'
import { StructureBilling } from '@/Structures/types'
import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined'
import { SWLoadingButton } from '@/components/LoadingButton'

const useStyles = makeStyles((theme) => ({
  paper: {
    minHeight: theme.spacing(70)
  },
  dialogActionsRoot: {
    '@media print': {
      display: 'none'
    }
  }
}))

type Props = {
  bill: StructureBilling
  onClose: () => void
  open: boolean
  structureName: string
}

export const Breakdown = ({ bill, structureName, open, onClose }: Props): JSX.Element => {
  const classes = useStyles()
  const dateFormatStr = 'LLL do, y'
  const formattedStartDate = format(new Date((bill.start_time || 0) * 1000), dateFormatStr)
  const formattedEndDate = format(new Date((bill.end_time || 0) * 1000), dateFormatStr)
  const title = `${structureName} (${formattedStartDate} - ${formattedEndDate})`

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
      maxWidth={'md'}
      fullWidth
      classes={{
        paper: classes.paper
      }}
    >
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <TableContainer component={Paper}>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Unit Name</TableCell>
                <TableCell>Property Name</TableCell>
                <TableCell>Volume</TableCell>
                <TableCell>Volume Unit</TableCell>
                <TableCell>Effective Rate</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right">Cost Units</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bill.units.sort(sortBy('name')).map((row) => (
                <TableRow key={row.id}>
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {bill.structure_name}
                  </TableCell>
                  <TableCell align="right">{formatNumber(row.unit_total_volume, 3)}</TableCell>
                  <TableCell component="th" scope="row">
                    {bill.volume_units}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {formatNumber(bill.effective_rate, 2)}
                  </TableCell>
                  <TableCell align="right">{`$${formatNumber(row.unit_cost_meter, 2)}`}</TableCell>
                  <TableCell component="th" scope="row">
                    {bill.cost_units}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell component="th" scope="row">
                  Total
                </TableCell>
                <TableCell component="th" scope="row" />
                <TableCell align="right">
                  {formatNumber(bill.units.reduce((acc, cur) => acc + cur.unit_total_volume, 0))}
                </TableCell>
                <TableCell component="th" scope="row" />
                <TableCell component="th" scope="row" />
                <TableCell align="right">{`$${formatNumber(
                  bill.units.reduce((acc, cur) => acc + cur.unit_cost_meter, 0)
                )}`}</TableCell>
                <TableCell component="th" scope="row" />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions classes={{ root: classes.dialogActionsRoot }}>
        <SWLoadingButtonOutlined
          onClick={() => {
            convertArrayToCsvString(
              bill.units.map((row) => ({
                'Unit Name': row.name,
                'Structure Name': bill.structure_name,
                Volume: formatNumber(row.unit_total_volume, 3),
                'Volume Units': bill.volume_units,
                'Effective Rate': formatNumber(bill.effective_rate, 2),
                Cost: formatNumber(row.unit_cost_meter, 2),
                'Cost Units': bill.cost_units
              }))
            ).then((csv) =>
              saveAs(new Blob([csv], { type: 'text/plain;charset=utf-8' }), `${title.replace(/ /g, '_')}.csv`)
            )
          }}
        >
          Download CSV
        </SWLoadingButtonOutlined>
        <SWLoadingButton onClick={window.print}>
          Print
        </SWLoadingButton>
      </DialogActions>
    </Dialog>
  )
}
